import { css } from '@emotion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';

// Schemas
import { ActivityKey, MinimalActivity } from 'redux/schemas/models/activity';

// Selectors
import { getCourseActivities } from 'redux/selectors/course';

// Components
import NvFormDropdown from 'shared/components/inputs/nv-form-dropdown';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import { NvDropdownTextItem } from 'shared/components/inputs/nv-dropdown';

// Styles
import { info, gray2, gray4, gray7, gray6 } from 'styles/global_defaults/colors';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { textLargeFontSize } from 'styles/global_defaults/fonts';

const styles = css`
  &.live-event-session-registered {
    background-color: ${gray7};
    border-radius: 10px;

    .selected-values {
      display: inline-block;
      margin-left: ${halfSpacing}px;
    }

    .dropdowns {
      visibility: hidden;
      display: flex;
      height: 0;

      .live-event {
        flex: 2;
      }

      .session {
        flex: 3;
      }

      .live-event, .session {
        .title {
          font-size: ${textLargeFontSize}px;
        }
      }
    }

    .info {
      flex: 1 0 auto;
      text-align: right;
      color: ${gray2};
      vertical-align: middle;
      line-height: 24px;
    }

    &.on {
      background-color: ${info};

      .dropdowns {
        visibility: visible;
        height: auto;
        margin-bottom: ${halfSpacing}px;
      }
    }

    &.disabled {
      background-color: ${gray6};
      display: flex;

      .info {
        color: ${gray4};
        display: inherit;
        justify-content: flex-end;
        align-items: center;
      }

      .dropdowns {
        display: none;
      }
    }
  }
`;

type LiveSessionWithSubSessions = MinimalActivity & {
  sessions?: {
    id: number,
    title: string,
  }[],
};

const LiveEventSessionRegistered = () => {
  const { setValue, watch } = useFormContext();
  const courseActivities = useSelector((state) => getCourseActivities(state));

  const [liveEventSessionRegisteredChecked, liveEvent, session] = watch([
    'liveEventSessionRegisteredChecked',
    'liveEventSessionRegisteredEvent',
    'liveEventSessionRegisteredSession'
  ]);

  const isEnabled = liveEventSessionRegisteredChecked;

  // Only multi-session live events (more than one session)
  const liveEventsList: LiveSessionWithSubSessions[] = useMemo(
    () => (courseActivities[ActivityKey.LIVE_SESSION] ?? [])
      .filter((liveSession: LiveSessionWithSubSessions) => (liveSession.sessions?.length ?? 0) > 1),
    [courseActivities],
  );

  // Build live event dropdown options
  const liveEventOptions: NvDropdownTextItem[] = useMemo(() => liveEventsList.map((liveSession: LiveSessionWithSubSessions) => ({
    id: liveSession.id,
    type: 'text',
    text: liveSession.title,
    value: liveSession.id,
    callback: () => {
      setValue('liveEventSessionRegisteredSession', null, { shouldValidate: true, shouldDirty: true });
    },
  })), [liveEventsList, setValue]);
  const sessionOptions: NvDropdownTextItem[] = useMemo(() => {
    if (!liveEvent) return [];

    const liveEventId = liveEvent.value ?? liveEvent.id;
    const selectedLiveEvent = liveEventsList.find(item => item.id === liveEventId);
    const sessions = selectedLiveEvent?.sessions ?? [];

    return sessions.map(sessionItem => ({
      id: sessionItem.id,
      type: 'text',
      text: sessionItem.title,
      value: sessionItem.id,
    }));
  }, [liveEvent, liveEventsList]);

  const noLiveEvents = liveEventsList.length === 0;

  // Clear session when checkbox is unchecked
  useEffect(() => {
    if (!liveEventSessionRegisteredChecked) {
      setValue('liveEventSessionRegisteredEvent', null, { shouldValidate: true, shouldDirty: true });
      setValue('liveEventSessionRegisteredSession', null, { shouldValidate: true, shouldDirty: true });
    }
  }, [liveEventSessionRegisteredChecked, setValue]);

  useEffect(() => {
    if (!session || liveEvent || !liveEventSessionRegisteredChecked) {
      return;
    }

    const sessionId = session.value ?? session.id;
    const parentEvent = liveEventsList.find(item => item.sessions?.some(s => s.id === sessionId));

    if (parentEvent) {
      setValue('liveEventSessionRegisteredEvent', { id: parentEvent.id, value: parentEvent.id }, { shouldValidate: true, shouldDirty: false });
    }
  }, [session, liveEvent, liveEventsList, setValue, liveEventSessionRegisteredChecked]);

  return (
    <div css={styles} className={`px-4 py-2 mb-4 live-event-session-registered ${isEnabled ? 'on' : ''}`}>
      <div className='inline-block mr-2 checkbox-wrapper'>
        <NvCheckbox
          withForm
          name='liveEventSessionRegisteredChecked'
          label={t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.HAS_REGISTERED()}
          labelClassName='text-large-regular pl-6 mr-2'
        />
      </div>
      {noLiveEvents && (
        <div className='info text-small text-gray-2 font-weight-bold'>
          {t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.NO_MULTI_SESSION()}
        </div>
      )}
      <div className='dropdowns ml-6'>
        <NvFormDropdown
          name='liveEventSessionRegisteredEvent'
          className='live-event'
          items={liveEventOptions}
          title={t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.SELECT_EVENT()}
        />
        <NvFormDropdown
          maxWidth100
          name='liveEventSessionRegisteredSession'
          className='session ml-2'
          items={sessionOptions}
          title={t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.SELECT_SESSION()}
          disabled={liveEvent == null}
        />
      </div>
    </div>
  );
};

export default LiveEventSessionRegistered;
